import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { PersonIcon, SparkleIcon } from '@modernloop/shared/icons';
import { Avatar, Box, CircularProgress, Stack, Typography } from '@mui/material';

import type { AiTimelineMessage } from './useAiSchedulingStream';

type Props = {
  message: AiTimelineMessage;
};

const TimelineMessage: React.FC<Props> = ({ message }) => {
  const { t } = useTranslation();
  const isAgent = message.role === 'ASSISTANT' || message.role === 'SYSTEM';

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: isAgent ? 'success.main' : 'primary.main',
          fontSize: 16,
        }}
      >
        {isAgent ? <SparkleIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
      </Avatar>

      <Stack spacing={0.5} flex={1} minWidth={0}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {isAgent
              ? t('hiring-dashboard-candidates:candidates.aiTimeline.aiAgent')
              : t('hiring-dashboard-candidates:candidates.aiTimeline.you')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </Typography>
          {message.isStreaming && <CircularProgress size={12} />}
        </Stack>

        <Box
          sx={{
            bgcolor: isAgent ? 'background.paper' : 'primary.50',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            border: '1px solid',
            borderColor: isAgent ? 'divider' : 'primary.200',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content || (message.isStreaming ? '...' : '')}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

export default TimelineMessage;
